import { useCallback, useMemo, useRef, useState } from 'react';

import {
  REVIEW_WIZARD_STEP_ORDER,
  ReviewQuestionStatus,
  ReviewWizardStep,
  type ReviewQuestion,
} from '../logic/reviewTypes';
import {
  validateSubQuestions,
  type SubQuestionViolation,
} from '../../../shared/domain/subQuestionValidation';
import {
  validateQuestionConfig,
  validateCriteria,
  type QuestionConfigViolation,
  type CriteriaViolation,
} from '../../../shared/domain/questionConfigValidation';

/** Public surface of {@link useReviewWizard}. */
export interface ReviewWizardApi {
  /** The full draft list, in display order. */
  readonly questions: readonly ReviewQuestion[];
  /** Client-side id of the currently-selected question. */
  readonly selectedId: string;
  /** The currently-selected question — always a real entry while the list is
   *  non-empty (selection falls back to the first question if it drifts). */
  readonly selectedQuestion: ReviewQuestion | undefined;
  /** Select a question by its client-side id. */
  readonly selectQuestion: (id: string) => void;
  /** Write an edited question back into the list (matched by `id`). The step
   *  components hand up a whole new `ReviewQuestion`. */
  readonly onQuestionChange: (next: ReviewQuestion) => void;
  /** Remove an unpublished draft and reselect a neighbour. */
  readonly deleteQuestion: (id: string) => void;

  /** The active wizard step. */
  readonly currentStep: ReviewWizardStep;
  /** Jump to a specific step. */
  readonly goToStep: (step: ReviewWizardStep) => void;
  /** Advance one step (clamped at the last). */
  readonly goNext: () => void;
  /** Go back one step (clamped at the first). */
  readonly goPrev: () => void;
  /** "Skip for now" — leave this whole question for later and select the next
   *  unpublished draft, wrapping circularly past the end (published drafts are
   *  skipped). No-op only when no other unpublished draft exists. */
  readonly skipForNow: () => void;
  readonly isFirstStep: boolean;
  readonly isLastStep: boolean;

  /**
   * Validate the selected draft for publishing and, when publishable, return
   * the target `ReviewQuestion` to publish — WITHOUT mutating state. Returns
   * `null` when there's no selectable target or any of the three validators
   * report a violation (same gate as {@link canPublish}). The caller then
   * performs the async create (media upload + API) and, on success, calls
   * {@link markPublished}. Validation stays in the hook so banner and gate
   * never disagree; the async orchestration lives in the page.
   */
  readonly beginPublish: () => ReviewQuestion | null;

  /**
   * Mark a draft PUBLISHED with its real Helios Question ID and advance
   * selection to the next unpublished draft. Matched BY `id`, so it's a safe
   * no-op if that draft was deleted or the selection moved while the create
   * request was in flight (re-check-intent on async completion).
   */
  readonly markPublished: (id: string, questionId: string) => void;

  /** LIVE sub-question violations for the selected draft — recomputed every
   *  render from the draft's current sub-questions, so the naming banner
   *  appears the instant a select sub drops below two options and clears the
   *  instant the author adds the second (no publish click needed to reveal or
   *  clear it). `null` when the selected draft is publishable. The publish gate
   *  reads the same validator, so the two can never disagree. */
  readonly publishBlock: readonly SubQuestionViolation[] | null;

  /** LIVE question-CONFIGURATION violations for the selected draft (overview /
   *  division / audit score / tags), recomputed every render from the draft's
   *  current `configuration`. `null` when the configuration is publishable.
   *  The publish gate reads the same validator. */
  readonly configBlock: readonly QuestionConfigViolation[] | null;

  /** LIVE audit-CRITERIA violations for the selected draft (the five required
   *  rich-text fields), recomputed every render from the draft's current
   *  `content.questionDetails`. `null` when the criteria are publishable. The
   *  publish gate reads the same validator. */
  readonly criteriaBlock: readonly CriteriaViolation[] | null;

  /** Per-step "has blocking validation errors" flags for the selected draft,
   *  keyed by wizard step — AUDIT_CRITERIA ← criteriaBlock, SUB_QUESTIONS ←
   *  publishBlock, QUESTION_CONFIGURATION ← configBlock. The stepper reads this
   *  to show a per-step warning marker. A Map (not a keyed object) so the
   *  union-typed step lookup doesn't trip security/detect-object-injection. */
  readonly stepHasErrors: ReadonlyMap<ReviewWizardStep, boolean>;

  /** Whether the selected draft can be published — true only when ALL three
   *  blocks are clear. The Save & publish button disables on `!canPublish`. */
  readonly canPublish: boolean;
}

/** Reference-stable default for the optional `mandatoryTagGroups` param, so an
 *  omitted argument doesn't re-run the config memo every render. */
const EMPTY_MANDATORY_TAG_GROUPS: readonly string[] = [];

/** Reference-stable empty seed — the runtime default when NO generation handed
 *  questions over (direct navigation). The wizard renders the empty-review
 *  state in that case; there is no mock/sample fallback in the real workflow. */
const EMPTY_SEED_QUESTIONS: readonly ReviewQuestion[] = [];

/** First step in the wizard order — the reset/default position. */
const FIRST_STEP = REVIEW_WIZARD_STEP_ORDER[0] ?? ReviewWizardStep.AUDIT_CRITERIA;

/** Pick the id to select after `removedOrPublishedId` leaves the selectable
 *  set: the next selectable (NEEDS_REVIEW) question after it, else the previous
 *  one, else '' when none remain. `list` is the post-transition list. */
function nextSelectableId(list: readonly ReviewQuestion[], anchorIndex: number): string {
  for (let i = anchorIndex; i < list.length; i += 1) {
    const candidate = list.at(i);
    if (candidate && candidate.status === ReviewQuestionStatus.NEEDS_REVIEW) {
      return candidate.id;
    }
  }
  for (let i = anchorIndex - 1; i >= 0; i -= 1) {
    const candidate = list.at(i);
    if (candidate && candidate.status === ReviewQuestionStatus.NEEDS_REVIEW) {
      return candidate.id;
    }
  }
  // No unpublished draft left: fall back to the first question (if any) so the
  // panel still shows a selection, even though its step editors are disabled.
  return list.at(0)?.id ?? '';
}

/**
 * State orchestrator for the "Review & publish Astra questions" wizard.
 *
 * Owns the three pieces of state the (fully-controlled) review components need
 * — the draft list, the selected question id, and the active wizard step — and
 * exposes the actions the page wires to the side panel, the stepper, and the
 * three step editors.
 *
 * Seeded ONLY from the questions a real Astra generation handed over (via
 * `seedQuestions`). There is no mock/sample fallback in the runtime path:
 * direct navigation with no generation renders the empty-review state. Publish
 * flips status locally with the real Question ID returned by the create call.
 */
export function useReviewWizard(
  /** Tag groups the catalog flags mandatory-for-questions, used by the config
   *  validator's all-mandatory-tags rule. The page sources these from the tags
   *  query and passes them in (the hook owns no data fetching). Defaults to
   *  `[]` so the mandatory rule can't false-positive before the catalog
   *  resolves. Reference-stable at the call site (see the page's memo). */
  mandatoryTagGroups: readonly string[] = EMPTY_MANDATORY_TAG_GROUPS,
  /** Questions a real Astra generation handed over (from the generate screen's
   *  nav state) to seed the wizard. The ONLY seed source — there is no mock
   *  fallback in the runtime path:
   *    - omitted / empty (`[]`) → NO questions to review. This is both the
   *      direct-navigation case and a successful generation that produced zero
   *      questions; either way the wizard renders the empty-review state rather
   *      than fabricated sample questions.
   *    - a non-empty array → seed from it verbatim.
   *  Tests inject their own fixtures through this param (see the shared mock
   *  fixture under logic/__tests__). Reference-stable at the call site (the
   *  page passes the nav-state array, itself stable, or omits the arg). Read
   *  once in the initializer below — later identity changes don't re-seed, so a
   *  reviewer's in-progress edits are never clobbered by a re-render. */
  seedQuestions: readonly ReviewQuestion[] = EMPTY_SEED_QUESTIONS,
): ReviewWizardApi {
  // The wizard seeds ONLY from questions a real generation handed over. There
  // is no mock/sample fallback in the runtime path — direct navigation (no
  // generation) arrives with the empty default and renders the empty-review
  // state, so a reviewer is never shown fabricated questions as if they were
  // the agent's output. (Tests inject their own fixtures via this param.)
  // Each generated question already carries its own source-SOP doc link (the
  // SOP the agent cited, marked `isSourceSop`), so the hook needs no
  // SOP-prefill parameter.
  // Read once in a lazy initializer so the array isn't re-read every render;
  // later identity changes don't re-seed and clobber in-progress edits.
  const buildInitialQuestions = (): readonly ReviewQuestion[] => seedQuestions;
  const [questions, setQuestions] =
    useState<readonly ReviewQuestion[]>(buildInitialQuestions);
  // Mirror of `questions`, synced during render, so async completions
  // (markPublished runs after the publish request) can read the LATEST list
  // instead of the snapshot captured when the publish started. Without this a
  // concurrent edit made while publishing was in flight would be clobbered.
  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  const [selectedId, setSelectedId] = useState<string>(
    () => buildInitialQuestions().at(0)?.id ?? '',
  );
  // Mirror of `selectedId`, synced during render — same reason as questionsRef:
  // markPublished runs after the publish request, by which point the user may
  // have re-selected a different draft. Reading the ref (not the closure) means
  // "advance selection only if the published draft is STILL selected" tests
  // against the live selection, not the one captured when publishing began.
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const [currentStep, setCurrentStep] = useState<ReviewWizardStep>(FIRST_STEP);

  // The selected question, resolved against the current list. Falls back to the
  // first entry if the id drifted (e.g. after a delete) so the step editors —
  // which require a `question` — never receive undefined while drafts remain.
  const selectedQuestion = useMemo(
    () => questions.find((q) => q.id === selectedId) ?? questions.at(0),
    [questions, selectedId],
  );

  // LIVE publish-block: recomputed from the selected draft's current
  // sub-questions on every render (mirrors the perform-audit close gate's
  // derived violations). It shows the instant a select sub has <2 options and
  // clears the instant the author adds the second — no publish click needed to
  // reveal OR to clear it. `null` when the draft is publishable. The publish
  // gate reads the SAME validator, so the banner and the gate can never
  // disagree. (Mid-edit noise is bounded by the options editor's own inline
  // "add at least two options" hint sitting right under the field.)
  const publishBlock = useMemo<readonly SubQuestionViolation[] | null>(() => {
    if (!selectedQuestion) return null;
    const violations = validateSubQuestions(selectedQuestion.content.subQuestions ?? []);
    return violations.length > 0 ? violations : null;
  }, [selectedQuestion]);

  // LIVE configuration block — same live/derived contract as publishBlock, for
  // the question-level config fields. `null` when the configuration is
  // publishable.
  const configBlock = useMemo<readonly QuestionConfigViolation[] | null>(() => {
    if (!selectedQuestion) return null;
    const violations = validateQuestionConfig(
      selectedQuestion.configuration,
      mandatoryTagGroups,
    );
    return violations.length > 0 ? violations : null;
  }, [selectedQuestion, mandatoryTagGroups]);

  // LIVE audit-criteria block — same contract, for the five required rich-text
  // fields. `null` when the criteria are publishable.
  const criteriaBlock = useMemo<readonly CriteriaViolation[] | null>(() => {
    if (!selectedQuestion) return null;
    const violations = validateCriteria(selectedQuestion.content);
    return violations.length > 0 ? violations : null;
  }, [selectedQuestion]);

  // Per-step error flags, derived from the three blocks above — the stepper
  // reads this to mark steps with validation errors. Memoized so the Map
  // identity is stable across renders (the stepper takes it as a prop).
  const stepHasErrors = useMemo<ReadonlyMap<ReviewWizardStep, boolean>>(
    () =>
      new Map([
        [ReviewWizardStep.AUDIT_CRITERIA, criteriaBlock !== null],
        [ReviewWizardStep.SUB_QUESTIONS, publishBlock !== null],
        [ReviewWizardStep.QUESTION_CONFIGURATION, configBlock !== null],
      ]),
    [criteriaBlock, publishBlock, configBlock],
  );

  // The draft is publishable only when every step is clear. The Save & publish
  // button disables on `!canPublish`; the publishSelected gate is the backstop.
  const canPublish =
    criteriaBlock === null && publishBlock === null && configBlock === null;

  const selectQuestion = useCallback((id: string) => {
    setSelectedId(id);
    // Re-entering a draft starts the wizard at the first step.
    setCurrentStep(FIRST_STEP);
  }, []);

  const onQuestionChange = useCallback((next: ReviewQuestion) => {
    setQuestions((prev) => prev.map((q) => (q.id === next.id ? next : q)));
  }, []);

  const deleteQuestion = useCallback(
    (id: string) => {
      // Pure list removal — no selection side effects inside the updater
      // (StrictMode double-invokes updaters; side effects must not live there).
      setQuestions((prev) => prev.filter((q) => q.id !== id));

      // Deleting a draft that ISN'T the selected one leaves selection and step
      // untouched — nothing else to do.
      if (id !== selectedId) return;

      // The selected draft is going away: reselect a neighbour and reset the
      // wizard to the first step, matching selectQuestion / skipForNow /
      // publishSelected (every selection change re-enters at FIRST_STEP).
      // Both decisions are computed here from the id argument, outside the
      // setSelectedId updater.
      const remaining = questions.filter((q) => q.id !== id);
      const removedIndex = questions.findIndex((q) => q.id === id);
      setSelectedId(nextSelectableId(remaining, Math.max(0, removedIndex)));
      setCurrentStep(FIRST_STEP);
    },
    [questions, selectedId],
  );

  const goToStep = useCallback((step: ReviewWizardStep) => {
    setCurrentStep(step);
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep((step) => {
      const index = REVIEW_WIZARD_STEP_ORDER.indexOf(step);
      return REVIEW_WIZARD_STEP_ORDER.at(index + 1) ?? step;
    });
  }, []);

  const goPrev = useCallback(() => {
    setCurrentStep((step) => {
      const index = REVIEW_WIZARD_STEP_ORDER.indexOf(step);
      return index > 0 ? (REVIEW_WIZARD_STEP_ORDER.at(index - 1) ?? step) : step;
    });
  }, []);

  // Leave this whole question for later and jump to the next unpublished draft,
  // searching CIRCULARLY from the current position (published drafts aren't
  // selectable, so they're skipped; the search wraps past the end back to the
  // top). No-op only when there is no OTHER unpublished draft to move to.
  const skipForNow = useCallback(() => {
    const total = questions.length;
    const currentIndex = questions.findIndex((q) => q.id === selectedId);
    for (let offset = 1; offset <= total; offset += 1) {
      const candidate = questions.at((currentIndex + offset) % total);
      if (
        candidate &&
        candidate.id !== selectedId &&
        candidate.status === ReviewQuestionStatus.NEEDS_REVIEW
      ) {
        setSelectedId(candidate.id);
        setCurrentStep(FIRST_STEP);
        return;
      }
    }
  }, [questions, selectedId]);

  const beginPublish = useCallback((): ReviewQuestion | null => {
    // Guard: a missing or already-published selection has nothing to publish.
    const target = questions.find((q) => q.id === selectedId);
    if (!target || target.status === ReviewQuestionStatus.PUBLISHED) return null;

    // Validation gate: the draft can't publish while ANY of its three
    // validators report a violation — sub-questions (e.g. a select sub with <2
    // options), the question configuration (overview / division / score /
    // tags), or the audit criteria (the five required rich-text fields). The
    // review flow has no per-field Save, so this is the single enforcement
    // point. The banner shows all three live (derived above from the same
    // validators), so a blocked click returns null — the user is looking at
    // what to fix. Recomputed here (not read from the render-time memos) so the
    // gate can't race a stale memo; one pure source of truth keeps banner and
    // gate in lockstep.
    if (
      validateSubQuestions(target.content.subQuestions ?? []).length > 0 ||
      validateQuestionConfig(target.configuration, mandatoryTagGroups).length > 0 ||
      validateCriteria(target.content).length > 0
    ) {
      return null;
    }

    return target;
  }, [selectedId, questions, mandatoryTagGroups]);

  const markPublished = useCallback((id: string, questionId: string) => {
    // markPublished runs AFTER an async boundary (the create request). Reading
    // the `questions` captured at call time and writing it back would clobber
    // any edit the author made to another draft while the request was in
    // flight. So derive everything from the LATEST list inside functional
    // updaters (rule: updaters stay pure; emissions derive from current
    // state, never a captured snapshot).
    //
    // Matched BY id (not "the selected draft"): the request may have resolved
    // after the user selected or deleted a different draft, so only the
    // originating draft — if it still exists and isn't already published —
    // flips. No-op otherwise (re-check intent on completion).
    // Read the LATEST list from the ref (synced during render), not the
    // closure's captured `questions`, so a concurrent edit isn't clobbered.
    const latest = questionsRef.current;
    const anchor = latest.findIndex(
      (q) => q.id === id && q.status !== ReviewQuestionStatus.PUBLISHED,
    );
    if (anchor < 0) return; // nothing to publish anymore — pure no-op

    // Functional updater (pure, StrictMode-safe) flips the one draft on the
    // freshest list; it never overwrites concurrent edits because it maps
    // over `prev`, not a snapshot.
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === id ? { ...q, status: ReviewQuestionStatus.PUBLISHED, questionId } : q,
      ),
    );

    // Advance selection past the just-published draft ONLY if it is STILL the
    // selected one; publishing a background draft (or one the user navigated
    // away from mid-flight) leaves their place. Compared against the live
    // selection via the ref, not the closure captured when publishing began.
    // Computed outside any setState updater so the updaters stay pure.
    if (id === selectedIdRef.current) {
      const published = latest.map((q) =>
        q.id === id ? { ...q, status: ReviewQuestionStatus.PUBLISHED, questionId } : q,
      );
      setSelectedId(nextSelectableId(published, anchor + 1));
      setCurrentStep(FIRST_STEP);
    }
  }, []);

  const currentIndex = REVIEW_WIZARD_STEP_ORDER.indexOf(currentStep);

  return {
    questions,
    selectedId,
    selectedQuestion,
    selectQuestion,
    onQuestionChange,
    deleteQuestion,
    currentStep,
    goToStep,
    goNext,
    goPrev,
    skipForNow,
    isFirstStep: currentIndex <= 0,
    isLastStep: currentIndex === REVIEW_WIZARD_STEP_ORDER.length - 1,
    beginPublish,
    markPublished,
    publishBlock,
    configBlock,
    criteriaBlock,
    stepHasErrors,
    canPublish,
  };
}
